/**
 * 레이아웃 관련 스타일 컴포넌트
 */
import styled from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 92px);
  background-color: var(--bg-primary);
  overflow: hidden;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    height: calc(100vh - 52px);
  }

  @media (max-height: 500px) {
    height: calc(100vh - 32px);
  }
`;

export const ViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  background-color: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--panel-shadow);
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;

  -webkit-tap-highlight-color: transparent;

  &:focus, &:focus-visible {
    outline: none;
  }

  @media (max-width: 768px) {
    width: calc(100% - 20px);
    margin: 0 10px 10px 10px;
    padding: ${props => props.theme.spacing.md};
  }
`;

export const BookViewerSplit = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 20px;
  min-height: 0;
  overflow: hidden;
  padding: 0 ${props => props.theme.spacing['4xl']} ${props => props.theme.spacing['4xl']} ${props => props.theme.spacing['4xl']};
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const BookContentPanel = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 50vh;
  }
`;

export const DropOverlayContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 500;
  display: flex;
  flex-direction: row;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

export const DropZoneHalf = styled.div<{ $isDragOver?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${props => props.$isDragOver ? 'var(--drop-zone-bg, #f0f0f0)' : 'transparent'};
  border: ${props => props.$isDragOver ? '2px dashed var(--primary-color, #3b82f6)' : '2px dashed transparent'};
  transition: all 0.2s;
  min-height: 0;

  &:first-child {
    border-right-width: ${props => props.$isDragOver ? '1px' : '0'};
  }
  &:last-child {
    border-left-width: ${props => props.$isDragOver ? '1px' : '0'};
  }
`;

export const AttachmentPanelWrapper = styled.div<{ $show?: boolean; $onLeft?: boolean }>`
  flex: ${props => (props.$show ? 1 : 0)};
  min-width: ${props => (props.$show ? 0 : 0)};
  width: ${props => (props.$show ? 'auto' : 0)};
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${props => (props.$show && !props.$onLeft ? '0 8px 0 0' : '8px 0 0 0')};
  border: 1px solid ${props => props.theme.colors.border.ce};

  @media (max-width: 768px) {
    min-height: ${props => (props.$show ? 300 : 0)}px;
    border-top: ${props => (props.$show ? '1px solid #DBDBDB' : 'none')};
  }
`;

export const PagesContainer = styled.div<{ $pagesPerView?: 1 | 2 }>`
  flex: 1;
  position: relative;
  width: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: hidden;

  /* CSS Multi-Column Layout */
  column-count: ${props => props.$pagesPerView === 1 ? 1 : 2};
  column-gap: ${props => props.$pagesPerView === 1 ? '0' : '48px'};
  column-fill: auto;
  column-rule: ${props => props.$pagesPerView === 1 ? 'none' : `1px solid ${props.theme.colors.border.de}`};
`;

// 기존 PageContainer - CSS multi-column 전환 후 사용하지 않음
// 호환성을 위해 유지 (추후 제거 예정)
export const PageContainer = styled.div`
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 20px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%; /* 부모 높이를 명시적으로 상속 */
`;

// CSS Multi-Column용 콘텐츠 래퍼 (폰트 설정 상속용 루트)
export const ColumnContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  font-family: ${props => `var(--viewer-font-family, inherit)`};
  font-size: ${props => `var(--viewer-font-size, ${props.theme.fontSize.base})`};
  font-weight: ${props => `var(--viewer-font-weight, ${props.theme.fontWeight.normal})`};
  letter-spacing: ${props => `var(--viewer-letter-spacing, 0em)`};
  line-height: ${props => `var(--viewer-line-height, 1.8)`};

  &:focus, &:focus-visible {
    outline: none;
  }
`;

export const PageDivider = styled.div<{ $height?: number; $top?: number }>`
  position: absolute;
  left: 50%;
  ${props => props.$top !== undefined ? `top: ${props.$top}px;` : 'top: 0;'}
  width: 1px;
  background-color: #DBDBDB;
  transform: translateX(-50%);
  z-index: 1;
  ${props => props.$height ? `height: ${props.$height}px;` : 'height: 100%;'}
`;

export const ContentWrapper = styled.div`
  width: 100%;
  height: 100%; /* 부모 높이를 상속 */
  position: relative;
  overflow: hidden; /* 오버플로우 시 숨김 (스크롤 없음) */
`;

// 실제 콘텐츠 높이 측정용 (flex로 늘어나지 않음)
export const ContentInner = styled.div`
  width: 100%;
  position: relative;
`;

export const ComponentContainer = styled.div`
  break-inside: avoid; /* CSS Multi-Column에서 컴포넌트가 잘리지 않도록 */
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  line-height: inherit;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.gray[600]};
`;

export const PageLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--bg-primary);
  z-index: 10;
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.gray[600]};
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--text-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: ${props => props.theme.colors.error[600]};
  gap: ${props => props.theme.spacing.md};
`;

export const PageNumberWrapper = styled.div`
  padding-bottom: 16px;
`;

export const PageNumberMarker = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0;
  margin-bottom: 0;
  position: relative;
  break-inside: avoid; /* CSS Multi-Column에서 잘리지 않도록 */
  break-after: auto;
  
  &::before {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    width: 120px;
    height: 1px;
    border-top: 1px dashed ${props => props.theme.colors.border.be};
    transform: translateY(-50%);
  }
`;

export const PageNumberMarkerText = styled.span`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.gray[600]};
  font-weight: 600;
  position: relative;
  z-index: 1;
  background-color: ${props => props.theme.colors.border.ee};
  border-radius: ${props => props.theme.borderRadius.full};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.lg};
  letter-spacing: 0.01em;
`;

export const PageContinuedText = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.primary[500] || '#3b82f6'};
  font-weight: 500;
  position: relative;
  z-index: 1;
  background-color: var(--bg-secondary);
  padding: 4px 12px;
  border-radius: 4px;
`;export const PageContinuedMarker = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  margin-top: 12px;
  border-top: 1px dashed ${props => props.theme.colors.gray[300]};
`;